import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import AdminLayout from './components/AdminLayout';
import { getAdminLiveRooms, openRoomNow, AdminLiveRoomRow } from '../../services/liveQuizService';
import { useServerCountdown } from '../../hooks/useServerCountdown';

const ROOMS_QUERY_KEY = ['admin', 'liveRooms'];
const RUNNING_STATUSES = new Set(['lobby', 'question', 'reveal', 'scoreboard']);
const FINISHED_STATUSES = new Set(['finished', 'abandoned']);

/**
 * Poll only while there's something to watch — a room mid-question is worth
 * refreshing every few seconds; a page of scheduled or finished rooms is not
 * going to change on its own.
 */
export function getAdminRoomsRefetchInterval(rows: AdminLiveRoomRow[]): number | false {
  return rows.some(row => RUNNING_STATUSES.has(row.status)) ? 10_000 : false;
}

function formatDateTime(iso: string): string {
  const date = new Date(iso);
  const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
  const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${weekday} ${time}`;
}

const StaleChip: React.FC<{ isStale: boolean }> = ({ isStale }) =>
  isStale ? (
    <Chip
      icon={<WarningAmberIcon />}
      label="Stale — no recent heartbeat"
      size="small"
      color="warning"
      sx={{ ml: 1 }}
    />
  ) : null;

const ScheduledRoomRow: React.FC<{ row: AdminLiveRoomRow; onOpenNow: (quizId: number) => void }> = ({
  row,
  onOpenNow
}) => {
  // Purely for a live-updating "starts in" display; the row itself is
  // recomputed fresh from the current time on every tick, never `Date.now()`
  // read once and left stale.
  const { label } = useServerCountdown(row.scheduledStartAt);

  return (
    <TableRow hover>
      <TableCell>
        <Typography fontWeight={600}>{row.quizTitle}</Typography>
        <StaleChip isStale={row.isStale} />
      </TableCell>
      <TableCell>{formatDateTime(row.scheduledStartAt)}</TableCell>
      <TableCell>{formatDateTime(row.lobbyOpensAt)}</TableCell>
      <TableCell align="right">Starts in {label}</TableCell>
      <TableCell align="right">
        {/* The RSVP feature exists to answer exactly this question. */}
        {row.signupCount} signed up
      </TableCell>
      <TableCell align="right">
        <Button
          size="small"
          variant="outlined"
          startIcon={<PlayCircleOutlineIcon />}
          onClick={() => onOpenNow(row.quizId)}
        >
          Open lobby now
        </Button>
      </TableCell>
    </TableRow>
  );
};

const RunningRoomRow: React.FC<{ row: AdminLiveRoomRow }> = ({ row }) => {
  const { label } = useServerCountdown(row.phaseEndsAt);

  return (
    <TableRow hover>
      <TableCell>
        <Typography component={RouterLink} to={`/admin/live/${row.roomId}`} fontWeight={600}>
          {row.quizTitle}
        </Typography>
        <StaleChip isStale={row.isStale} />
      </TableCell>
      <TableCell>{row.status}</TableCell>
      <TableCell align="right">
        {Math.min(row.currentQuestionIndex + 1, row.totalQuestions)} / {row.totalQuestions}
      </TableCell>
      <TableCell align="right">{row.participantCount}</TableCell>
      <TableCell align="right">{row.phaseEndsAt ? label : '—'}</TableCell>
    </TableRow>
  );
};

const FinishedRoomRow: React.FC<{ row: AdminLiveRoomRow }> = ({ row }) => (
  <TableRow hover>
    <TableCell>
      <Typography fontWeight={600}>{row.quizTitle}</Typography>
    </TableCell>
    <TableCell>{row.finishedAt ? formatDateTime(row.finishedAt) : '—'}</TableCell>
    <TableCell align="right">{row.playedCount}</TableCell>
    <TableCell align="right">
      {row.ratedCount}
      {row.ratedCount === 0 && (
        <Typography variant="caption" color="text.secondary" display="block">
          Too few mathletes finished to rate (field under three)
        </Typography>
      )}
    </TableCell>
  </TableRow>
);

interface RoomSectionProps {
  title: string;
  emptyMessage: string;
  columns: string[];
  rows: React.ReactNode;
  isEmpty: boolean;
}

const RoomSection: React.FC<RoomSectionProps> = ({ title, emptyMessage, columns, rows, isEmpty }) => (
  <Box sx={{ mb: 4 }}>
    <Typography variant="h6" component="h2" fontWeight={700} sx={{ mb: 1.5 }}>
      {title}
    </Typography>
    {isEmpty ? (
      <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">{emptyMessage}</Typography>
      </Paper>
    ) : (
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map(column => (
                <TableCell key={column} align={column === columns[0] ? 'left' : 'right'}>
                  {column}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>{rows}</TableBody>
        </Table>
      </TableContainer>
    )}
  </Box>
);

/**
 * The room list nothing links to yet. Grouped by what the admin actually
 * needs to know at each stage: will a scheduled room fire on time (and how
 * many students are expecting it), is a running room healthy, and did a
 * finished room actually rate.
 */
const LiveQuizConsolePage: React.FC = () => {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ROOMS_QUERY_KEY,
    queryFn: () => getAdminLiveRooms({ limit: 100 }),
    refetchInterval: query => getAdminRoomsRefetchInterval(query.state.data?.rooms ?? [])
  });

  const rows = data?.rooms ?? [];
  const scheduled = rows.filter(row => row.status === 'scheduled');
  const running = rows.filter(row => RUNNING_STATUSES.has(row.status));
  const finished = rows.filter(row => FINISHED_STATUSES.has(row.status));

  const handleOpenNow = async (quizId: number) => {
    await openRoomNow(quizId);
    queryClient.invalidateQueries({ queryKey: ROOMS_QUERY_KEY });
  };

  return (
    <AdminLayout>
      <Container maxWidth="lg">
        <Typography variant="h4" component="h1" fontWeight={700} sx={{ mb: 3 }}>
          Live Quiz Console
        </Typography>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">Failed to load live rooms. Please try again.</Alert>
        ) : (
          <>
            <RoomSection
              title="Scheduled"
              emptyMessage="Nothing scheduled right now."
              columns={['Quiz', 'Starts', 'Lobby opens', 'Countdown', 'Signups', '']}
              isEmpty={scheduled.length === 0}
              rows={scheduled.map(row => (
                <ScheduledRoomRow key={row.roomId} row={row} onOpenNow={handleOpenNow} />
              ))}
            />

            <RoomSection
              title="Running"
              emptyMessage="Nothing running right now."
              columns={['Quiz', 'Phase', 'Question', 'Playing', 'Phase ends in']}
              isEmpty={running.length === 0}
              rows={running.map(row => (
                <RunningRoomRow key={row.roomId} row={row} />
              ))}
            />

            <RoomSection
              title="Finished"
              emptyMessage="No finished events yet."
              columns={['Quiz', 'Finished', 'Played', 'Rated']}
              isEmpty={finished.length === 0}
              rows={finished.map(row => (
                <FinishedRoomRow key={row.roomId} row={row} />
              ))}
            />
          </>
        )}
      </Container>
    </AdminLayout>
  );
};

export default LiveQuizConsolePage;
